import { defineComponent as _defineComponent } from 'vue'
import { normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, vModelText as _vModelText, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-5c73e4c0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "import-ics-container" }
const _hoisted_2 = { class: "import-options" }
const _hoisted_3 = { class: "option-tabs" }
const _hoisted_4 = {
  key: 0,
  class: "file-upload"
}
const _hoisted_5 = {
  key: 0,
  class: "upload-prompt"
}
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "Drag and drop your .ics file here", -1))
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", null, "or", -1))
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "file-type" }, "Supported format: .ics", -1))
const _hoisted_9 = {
  key: 1,
  class: "file-info"
}
const _hoisted_10 = {
  key: 1,
  class: "url-import"
}
const _hoisted_11 = { class: "input-group" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 0,
  class: "preview-section"
}
const _hoisted_16 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", null, "Preview Events", -1))
const _hoisted_17 = { class: "import-options" }
const _hoisted_18 = { class: "checkbox-group" }
const _hoisted_19 = { class: "events-list" }
const _hoisted_20 = { class: "event-checkbox" }
const _hoisted_21 = ["value"]
const _hoisted_22 = { class: "event-details" }
const _hoisted_23 = { class: "event-date" }
const _hoisted_24 = { key: 0 }
const _hoisted_25 = {
  key: 0,
  class: "event-description"
}
const _hoisted_26 = {
  key: 1,
  class: "error-message"
}
const _hoisted_27 = { class: "actions" }
const _hoisted_28 = ["disabled"]

import { ref, computed } from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faFileUpload,
  faFileAlt,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { API_URL } from "../config";
import { getErrorMessage } from "../types";
import type { IcsPreviewEvent } from "../types";

// Register Font Awesome icons

export default /*#__PURE__*/_defineComponent({
  __name: 'ImportICSEvents',
  props: {
    calendarId: {}
  },
  emits: ["close", "import-complete"],
  setup(__props: any, { emit: __emit }) {

library.add(faFileUpload, faFileAlt, faTimes);

const props = __props;

const emit = __emit;

// State
const importMethod = ref<"file" | "url">("file");
const selectedFile = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const icsUrl = ref("");
const previewEvents = ref<IcsPreviewEvent[]>([]);
const selectedEvents = ref<string[]>([]);
const error = ref("");
const isFetching = ref(false);

const importOptions = ref({
  skipExisting: false,
  updateExisting: false,
});

// Computed
const canImport = computed(() => {
  return selectedEvents.value.length > 0;
});

// Methods
const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    selectedFile.value = input.files[0];
    processFile(input.files[0]);
  }
};

const handleFileDrop = (event: DragEvent) => {
  if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
    selectedFile.value = event.dataTransfer.files[0];
    processFile(event.dataTransfer.files[0]);
  }
};

const removeFile = () => {
  selectedFile.value = null;
  previewEvents.value = [];
  selectedEvents.value = [];
};

const processFile = async (file: File) => {
  if (!file.name.endsWith(".ics")) {
    error.value = "Please select a valid .ics file";
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axios.post(
      `${API_URL}/calendar/${props.calendarId}/events/import/preview`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    // Process events to determine types based on titles
    const processedEvents = response.data.map((event: IcsPreviewEvent) => {
      const title = event.title.toLowerCase();

      // Determine event type based on title
      if (title.includes("no school") || title.includes("closed")) {
        event.type = "closed";
      } else if (title.includes("early")) {
        event.type = "early";
      } else if (title.includes("late")) {
        event.type = "late_start";
      } else if (title.includes("shortened") || title.includes("short day")) {
        event.type = "shortened";
      }

      return event;
    });

    previewEvents.value = processedEvents;
    selectedEvents.value = processedEvents.map((event) => event.uid);
  } catch (err: unknown) {
    error.value = getErrorMessage(err, "Error processing file");
  }
};

const fetchFromUrl = async () => {
  if (!icsUrl.value) return;

  isFetching.value = true;
  error.value = "";

  try {
    const response = await axios.post(
      `${API_URL}/calendar/${props.calendarId}/events/import/preview`,
      { icsUrl: icsUrl.value },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    // Process events to determine types based on titles
    const processedEvents = response.data.map((event: IcsPreviewEvent) => {
      const title = event.title.toLowerCase();

      // Determine event type based on title
      if (title.includes("no school") || title.includes("closed")) {
        event.type = "closed";
      } else if (title.includes("early")) {
        event.type = "early";
      } else if (title.includes("late")) {
        event.type = "late_start";
      } else if (title.includes("shortened") || title.includes("short day")) {
        event.type = "shortened";
      }

      return event;
    });

    previewEvents.value = processedEvents;
    selectedEvents.value = processedEvents.map((event) => event.uid);
  } catch (err: unknown) {
    error.value = getErrorMessage(err, "Error fetching ICS file from URL");
    previewEvents.value = [];
    selectedEvents.value = [];
  } finally {
    isFetching.value = false;
  }
};

const confirmImport = async () => {
  try {
    const eventsToImport = previewEvents.value.filter((event) =>
      selectedEvents.value.includes(event.uid)
    );

    await axios.post(
      `${API_URL}/calendar/${props.calendarId}/events/import/confirm`,
      {
        events: eventsToImport,
        importOptions: importOptions.value,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );

    emit("import-complete");
    emit("close");
  } catch (err: unknown) {
    error.value = getErrorMessage(err, "Error importing events");
  }
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("button", {
          class: _normalizeClass(['tab-button', { active: importMethod.value === 'file' }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (importMethod.value = 'file'))
        }, " Upload File ", 2),
        _createElementVNode("button", {
          class: _normalizeClass(['tab-button', { active: importMethod.value === 'url' }]),
          onClick: _cache[1] || (_cache[1] = ($event: any) => (importMethod.value = 'url'))
        }, " Import from URL ", 2)
      ]),
      (importMethod.value === 'file')
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", {
              class: _normalizeClass(["upload-area", { 'has-file': selectedFile.value }]),
              onDragover: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["prevent"])),
              onDrop: _withModifiers(handleFileDrop, ["prevent"])
            }, [
              _createElementVNode("input", {
                type: "file",
                ref_key: "fileInput",
                ref: fileInput,
                accept: ".ics",
                onChange: handleFileSelect,
                class: "file-input",
                style: {"display":"none"}
              }, null, 544),
              (!selectedFile.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "file-upload",
                      class: "upload-icon"
                    }),
                    _hoisted_6,
                    _hoisted_7,
                    _createElementVNode("button", {
                      class: "btn-browse",
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (fileInput.value?.click()))
                    }, " Browse Files "),
                    _hoisted_8
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                    _createVNode(_unref(FontAwesomeIcon), {
                      icon: "file-alt",
                      class: "file-icon"
                    }),
                    _createElementVNode("p", null, _toDisplayString(selectedFile.value.name), 1),
                    _createElementVNode("button", {
                      class: "btn-remove",
                      onClick: removeFile
                    }, [
                      _createVNode(_unref(FontAwesomeIcon), { icon: "times" })
                    ])
                  ]))
            ], 34)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_10, [
            _createElementVNode("div", _hoisted_11, [
              _withDirectives(_createElementVNode("input", {
                type: "url",
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((icsUrl).value = $event)),
                placeholder: "Enter ICS file URL",
                class: "url-input"
              }, null, 512), [
                [_vModelText, icsUrl.value]
              ]),
              _createElementVNode("button", {
                class: "btn-fetch",
                onClick: fetchFromUrl,
                disabled: !icsUrl.value || isFetching.value
              }, [
                (isFetching.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_13, "Fetching..."))
                  : (_openBlock(), _createElementBlock("span", _hoisted_14, "Fetch"))
              ], 8, _hoisted_12)
            ])
          ]))
    ]),
    (previewEvents.value.length > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
          _hoisted_16,
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("label", null, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((importOptions.value.skipExisting) = $event))
                }, null, 512), [
                  [_vModelCheckbox, importOptions.value.skipExisting]
                ]),
                _createTextVNode(" Skip existing events ")
              ]),
              _createElementVNode("label", null, [
                _withDirectives(_createElementVNode("input", {
                  type: "checkbox",
                  "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((importOptions.value.updateExisting) = $event))
                }, null, 512), [
                  [_vModelCheckbox, importOptions.value.updateExisting]
                ]),
                _createTextVNode(" Update existing events ")
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_19, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewEvents.value, (event) => {
              return (_openBlock(), _createElementBlock("div", {
                key: event.uid,
                class: "event-item"
              }, [
                _createElementVNode("div", _hoisted_20, [
                  _withDirectives(_createElementVNode("input", {
                    type: "checkbox",
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((selectedEvents).value = $event)),
                    value: event.uid
                  }, null, 8, _hoisted_21), [
                    [_vModelCheckbox, selectedEvents.value]
                  ])
                ]),
                _createElementVNode("div", _hoisted_22, [
                  _createElementVNode("h4", null, _toDisplayString(event.title), 1),
                  _createElementVNode("p", _hoisted_23, [
                    _createTextVNode(_toDisplayString(formatDate(event.startDate)) + " ", 1),
                    (event.endDate && event.endDate !== event.startDate)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_24, " - " + _toDisplayString(formatDate(event.endDate)), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (event.description)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_25, _toDisplayString(event.description), 1))
                    : _createCommentVNode("", true)
                ])
              ]))
            }), 128))
          ])
        ]))
      : _createCommentVNode("", true),
    (error.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_26, _toDisplayString(error.value), 1))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_27, [
      _createElementVNode("button", {
        class: "btn-cancel",
        onClick: _cache[8] || (_cache[8] = ($event: any) => (_ctx.$emit('close')))
      }, "Cancel"),
      _createElementVNode("button", {
        class: "btn-import",
        onClick: confirmImport,
        disabled: !canImport.value
      }, " Import Selected Events ", 8, _hoisted_28)
    ])
  ]))
}
}

})